import { analytics } from './analytics';
import { OnboardingStepName } from '@constants/onboarding';
import { getUserId } from '@lib/auth';
import {
  ONBOARDING_STATUS,
  OnboardingSessionStepData,
} from '@shared-types/db-types';
import { postToApi } from './api';
import { PUBLIC_API } from './environment';

/** Response structure from the onboarding API when fetching user data */
interface OnboardingFetchResponse {
  session?: {
    stepData?: Partial<OnboardingSessionStepData>;
    status?: ONBOARDING_STATUS;
  };
  user?: {
    email?: string;
    firstName?: string;
    lastName?: string;
    phone?: string;
  };
}

export interface OnboardingStepSubmission {
  stepNumber: number;
  stepName: OnboardingStepName;
  stepData: Record<string, unknown>;
  totalSteps: number;
}

/**
 * Generic function to post data to the onboarding endpoint
 * @param data - The data to send to the onboarding endpoint
 * @param description - Description for logging and error tracking
 * @param analyticsContext - Context for error analytics tracking
 * @returns Promise with the API response or null on failure
 */
export const postToOnboarding = async (
  data: Record<string, unknown>,
  description: string,
  analyticsContext: Record<string, unknown>
) => {
  try {
    const sessionId = await getUserId();

    const requestData = {
      id: sessionId,
      timestamp: new Date().toISOString(),
      ...data,
    };

    const result = await postToApi(
      `${PUBLIC_API}/onboarding`,
      description,
      'POST',
      requestData,
      false
    );

    return result;
  } catch (error) {
    console.error(`Error ${description.toLowerCase()}:`, error);
    // Track the API error for monitoring
    analytics.trackError(error as Error, {
      context: analyticsContext.context,
      ...analyticsContext,
    });
    // Continue even if API call fails to not block user flow
    return null;
  }
};

export const submitOnboardingStep = async ({
  stepNumber,
  stepName,
  stepData,
  totalSteps,
}: OnboardingStepSubmission) => {
  // Track analytics first (regardless of API success/failure)
  analytics.trackOnboardingStep(stepNumber, stepData, totalSteps);

  // Extract user data if it exists in stepData and format properly
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { user, ...remainingStepData } = stepData as any;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const requestData: any = {
    stepNumber,
    stepName,
    stepData: remainingStepData,
  };

  // Add user data under user key if it exists
  if (user) {
    requestData.user = user;
  }

  return await postToOnboarding(requestData, 'Submit onboarding step', {
    context: 'onboarding_api_submission',
    stepNumber,
    stepName,
  });
};

/**
 * Updates the user's onboarding status in the database.
 * @param {ONBOARDING_STATUS} status - The new onboarding status to set.
 * @returns {Promise<null | any>} The updated onboarding session data or null if failed.
 */
export const updateOnboardingStatus = async (status: ONBOARDING_STATUS) => {
  return await postToOnboarding({ status }, 'Update onboarding status', {
    context: 'onboarding_status_update',
    status,
  });
};

/**
 * Fetches the user's onboarding data from the database.
 * @returns {Promise<OnboardingSessionStepData | null>} The user's onboarding data or null if failed.
 */
export const fetchOnboardingData =
  async (): Promise<OnboardingSessionStepData | null> => {
    const response = await postToOnboarding(
      { action: 'fetch' }, // Indicate we want to fetch existing data
      'Fetch onboarding data',
      {
        context: 'onboarding_data_fetch',
      }
    );

    // If the API returns a nested structure, we need to flatten it
    const typedResponse = response as OnboardingFetchResponse | null;
    if (typedResponse?.session && typedResponse?.user) {
      const flattenedData = {
        ...typedResponse.session.stepData,
        step: typedResponse.session.stepData?.step,
        status: typedResponse.session.status, // Extract status from session
        email: typedResponse.user.email,
        firstName: typedResponse.user.firstName,
        lastName: typedResponse.user.lastName,
        phone: typedResponse.user.phone,
      };
      return flattenedData as OnboardingSessionStepData;
    }

    return typedResponse as OnboardingSessionStepData | null;
  };
